import { Alert, Button, Card, Divider, Drawer, Form, Image, Input, Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { FileOutlined, FilePdfOutlined, UploadOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { openNotification } from '../../utils';
import AssignmentService from './../../services/AssignmentService';
import './assignment-detail.css';

type Attachment = {
    url: string;
    mimeType: string;
};

const { TextArea } = Input;

const AssignmentDetail: React.FC<any> = ({ item, getTrigger }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [assignmentAttachment, setAssignmentAttachment] = useState<Attachment | null>(null);
    const [responseAttachment, setResponseAttachment] = useState<Attachment | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [form] = Form.useForm();
    const isExpired = Boolean(item.isExpired) && !item.isDone;

    const showModal = () => {
        setFileList([]);
        form.setFieldsValue({ responseContent: item.responseContent });
        setIsModalOpen(true);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setFileList([]);
        form.resetFields();
    };

    useEffect(() => {
        if (!isModalOpen) {
            setAssignmentAttachment(null);
            setResponseAttachment(null);
            return;
        }

        let isActive = true;
        const objectUrls: string[] = [];
        const fetchAttachment = async (fileName: string, setAttachment: (attachment: Attachment) => void) => {
            try {
                const response = await AssignmentService.getFile(fileName);
                const mimeType = response.headers['content-type'] || 'application/octet-stream';
                const url = URL.createObjectURL(new Blob([response.data], { type: mimeType }));
                objectUrls.push(url);
                if (isActive) {
                    setAttachment({ url, mimeType });
                }
            } catch (error) {
                console.error('Dosya alınamadı:', error);
            }
        };

        if (item.image) {
            void fetchAttachment(item.image, setAssignmentAttachment);
        }
        if (item.responseImage) {
            void fetchAttachment(item.responseImage, setResponseAttachment);
        }

        return () => {
            isActive = false;
            objectUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [isModalOpen, item.image, item.responseImage]);

    const handleRespondSubmit = async () => {
        if (isExpired) {
            openNotification('warning', 'Süresi Doldu', 'Teslim süresi dolan ödeve cevap gönderemezsiniz.');
            return;
        }

        try {
            const values = await form.validateFields();
            if (!values.responseContent?.trim() && fileList.length === 0) {
                openNotification('warning', 'Uyarı', 'Cevap metni veya dosya ekleyiniz.');
                return;
            }

            setLoading(true);
            await AssignmentService.update(item.id, {
                responseContent: values.responseContent,
                isDone: true,
            }, fileList[0]);
            openNotification('success', 'Başarılı', item.responseContent || item.responseImage ? 'Cevap güncellendi.' : 'Ödeve cevap verildi.');
            getTrigger();
            handleCancel();
        } catch (error) {
            console.error('Ödev cevabı güncellenemedi:', error);
        } finally {
            setLoading(false);
        }
    };

    const uploadProps: UploadProps = {
        beforeUpload: (file) => {
            setFileList([file]);
            return false;
        },
        fileList,
        maxCount: 1,
        multiple: false,
        onRemove: () => setFileList([]),
    };

    const renderAttachment = (attachment: Attachment | null, fileName: string, title: string) => {
        if (!attachment) {
            return null;
        }

        const isPdf = attachment.mimeType.includes('application/pdf') || fileName.toLowerCase().endsWith('.pdf');
        if (isPdf) {
            return (
                <>
                    <Button icon={<FilePdfOutlined />} href={attachment.url} target="_blank" rel="noreferrer">
                        PDF&apos;i Aç
                    </Button>
                    <iframe title={title} src={attachment.url} style={{ border: 0, height: 480, marginTop: 12, width: '100%' }} />
                </>
            );
        }

        const isImage = attachment.mimeType.startsWith('image/')
            || /\.(avif|gif|jpe?g|png|svg|webp)$/i.test(fileName);
        if (isImage) {
            return <Image alt={title} src={attachment.url} fallback="error.png" />;
        }

        return (
            <Button icon={<FileOutlined />} href={attachment.url} target="_blank" rel="noreferrer">
                Dosyayı Aç / İndir
            </Button>
        );
    };

    return (
        <>
            <Button className="bg-saha-purple btn fw-700 font-color-white" onClick={showModal}>
                Detay
            </Button>

            <Drawer title="Ödev Detay" placement="right" onClose={handleCancel} open={isModalOpen} style={{ padding: 0, margin: 0, border: 'none' }}>
                <Card loading={loading} className="assignment-detail-card" style={{ border: 'none' }} bodyStyle={{ padding: 0, margin: 0, border: 'none' }}>
                    <h4>Ders</h4>
                    <p>{item.lessonName}</p>
                    <Divider />
                    <h4>Ödev İçeriği</h4>
                    <p>{item.content}</p>
                    {item.image &&
                        <>
                            <Divider />
                            <h4>Ödev Dosyası</h4>
                            {renderAttachment(assignmentAttachment, item.image, 'Ödev dosyası')}
                        </>
                    }
                    {item.responseImage &&
                        <>
                            <Divider />
                            <h4>Mevcut Cevap Dosyası</h4>
                            {renderAttachment(responseAttachment, item.responseImage, 'Cevap dosyası')}
                        </>
                    }

                    <Divider />
                    {isExpired ? (
                        <Alert
                            message="Süresi Doldu"
                            description="Teslim süresi dolduğu için bu ödeve cevap veya dosya ekleyemezsiniz."
                            showIcon
                            type="warning"
                        />
                    ) : (
                        <>
                            <h4>{item.responseContent || item.responseImage ? 'Cevabınızı Güncelleyin' : 'Ödevi Cevaplayın'}</h4>
                            <Form form={form} layout="vertical" onFinish={handleRespondSubmit}>
                                <Form.Item name="responseContent" label="Cevap">
                                    <TextArea />
                                </Form.Item>
                                <Form.Item label="Cevap Dosyası" extra="Tek bir dosya ekleyebilirsiniz.">
                                    <Upload {...uploadProps} listType="text">
                                        <Button icon={<UploadOutlined />}>Dosya Seç</Button>
                                    </Upload>
                                </Form.Item>
                                <Button htmlType="submit" type="primary" loading={loading}>
                                    {item.responseContent || item.responseImage ? 'Cevabı Güncelle' : 'Cevapla'}
                                </Button>
                            </Form>
                        </>
                    )}
                </Card>
            </Drawer>
        </>
    );
};

export default AssignmentDetail;
